import { useState } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const SKIN_LABELS = { dry_sensitive: "Dry / Sensitive", normal_combo: "Normal / Combination", oily: "Oily" };
const VARIATION_LABELS = { acne_prone: "Acne-prone", rosacea_prone: "Rosacea / redness-prone", none: "None" };

export default function SkinTypeQuiz() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [endOfDay, setEndOfDay] = useState("");
  const [rosacea, setRosacea] = useState(null);
  const [breakouts, setBreakouts] = useState(null);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const canSubmit = endOfDay && rosacea !== null && breakouts !== null;

  const submit = async () => {
    setErr(""); setSaving(true);
    try {
      const { data } = await api.post("/skin-type-quiz", {
        end_of_day_feel: endOfDay,
        rosacea,
        breakouts,
      });
      setResult(data);
    } catch (e) { setErr(formatErr(e)); }
    finally { setSaving(false); }
  };

  const useResult = async () => {
    setSaving(true);
    try {
      await api.put("/profile", { skin_type: result.skin_type, variation: result.variation });
      await refresh();
      nav("/dashboard", { replace: true });
    } finally { setSaving(false); }
  };

  return (
    <div className="App" data-testid="page-skin-type-quiz">
      <NavBar />
      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-14">
        <div className="aa-eyebrow mb-3">Skin type quiz</div>
        <h1 className="font-display text-4xl lg:text-5xl leading-tight">Let's figure out your <span className="serif-italic">skin type</span>.</h1>
        <p className="mt-4 text-[var(--aa-text-2)]">A quick self-check. <em>Try this on a day you've washed your face in the morning without applying any products afterward — that's the most accurate read.</em></p>

        {!result && (
          <div className="space-y-7 mt-10">
            <div className="aa-card p-6">
              <div className="aa-eyebrow mb-3">1. By the end of the day, my skin feels…</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  { k: "tight_dry", l: "Tight or dry — even a bit flaky or dull" },
                  { k: "greasy_oily", l: "Greasy / shiny all over" },
                  { k: "t_zone_shiny", l: "Shiny in the T-zone, normal on the cheeks" },
                  { k: "comfortable", l: "Comfortable — about the same as when I started" },
                ].map((opt) => (
                  <button
                    key={opt.k}
                    data-testid={`quiz-eod-${opt.k}`}
                    onClick={() => setEndOfDay(opt.k)}
                    className={`aa-card p-4 text-left text-sm transition-all ${endOfDay === opt.k ? "ring-2 ring-[var(--aa-text)] bg-[var(--aa-bg-2)]" : "hover:bg-[var(--aa-bg-2)]"}`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>

            <div className="aa-card p-6">
              <div className="aa-eyebrow mb-3">2. Do you tend to flush easily or have persistent redness on your cheeks, nose, or chin?</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <button type="button" data-testid="quiz-rosacea-yes" onClick={() => setRosacea(true)}
                  className={`aa-card p-4 text-left text-sm transition-all ${rosacea === true ? "ring-2 ring-[var(--aa-text)] bg-[var(--aa-bg-2)]" : "hover:bg-[var(--aa-bg-2)]"}`}>
                  Yes
                </button>
                <button type="button" data-testid="quiz-rosacea-no" onClick={() => setRosacea(false)}
                  className={`aa-card p-4 text-left text-sm transition-all ${rosacea === false ? "ring-2 ring-[var(--aa-text)] bg-[var(--aa-bg-2)]" : "hover:bg-[var(--aa-bg-2)]"}`}>
                  Not really
                </button>
              </div>
            </div>

            <div className="aa-card p-6">
              <div className="aa-eyebrow mb-3">3. Do you get frequent pimples or breakouts?</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <button type="button" data-testid="quiz-acne-yes" onClick={() => setBreakouts(true)}
                  className={`aa-card p-4 text-left text-sm transition-all ${breakouts === true ? "ring-2 ring-[var(--aa-text)] bg-[var(--aa-bg-2)]" : "hover:bg-[var(--aa-bg-2)]"}`}>
                  Yes
                </button>
                <button type="button" data-testid="quiz-acne-no" onClick={() => setBreakouts(false)}
                  className={`aa-card p-4 text-left text-sm transition-all ${breakouts === false ? "ring-2 ring-[var(--aa-text)] bg-[var(--aa-bg-2)]" : "hover:bg-[var(--aa-bg-2)]"}`}>
                  Not really
                </button>
              </div>
            </div>

            {err && <div className="text-[var(--aa-terra)] text-sm" data-testid="quiz-error">{err}</div>}
            <button data-testid="quiz-submit" disabled={!canSubmit || saving} onClick={submit}
              className="aa-cta-primary">
              {saving ? "Calculating…" : "See my result →"}
            </button>
          </div>
        )}

        {result && (
          <div className="aa-card p-7 mt-10 fade-in" data-testid="quiz-result">
            <div className="aa-eyebrow mb-2">Your result</div>
            <div className="font-display text-3xl">{SKIN_LABELS[result.skin_type]}</div>
            {result.variation !== "none" && (
              <div className="text-sm text-[var(--aa-text-2)] mt-1">Variation: {VARIATION_LABELS[result.variation]}</div>
            )}
            <p className="mt-4 text-[var(--aa-text-2)] leading-relaxed">{result.explanation}</p>
            <div className="flex gap-3 mt-7 flex-wrap">
              <button data-testid="quiz-use-result" onClick={useResult} className="aa-cta-primary">Use this result →</button>
              <button data-testid="quiz-retake" onClick={() => setResult(null)} className="aa-cta-secondary">Retake</button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
